import { memo, useState } from "react";
import { useLocalParticipant } from "@livekit/components-react";
import { twMerge } from "tailwind-merge";
import { useIsUserSpeaking } from "@/hooks/use-is-user-speaking";
import { useGlobalState } from "@/hooks/useGlobalState";
import { captureError } from "@/utils/logger";
import commentIcon from "/images/comment-icon.svg";
import shoppingBasket from "/images/shoping-basket.svg";
import microphone from "/images/microphone.svg";

const micWavesClasses =
  "widget-content nut-cursor-pointer nut-flex nut-items-center nut-justify-center nut-gap-1 nut-w-10 nut-mx-auto";

const MicSilent = () => (
  <div className={micWavesClasses}>
    {Array.from({ length: 7 }).map((_, index) => (
      <span key={index} className="nut-w-[3px] nut-h-[3px] nut-flex-shrink-0" />
    ))}
  </div>
);

const MicSpeaking = () => {
  const heights = [4, 24, 40, 18, 14, 24, 4];

  return (
    <div className={micWavesClasses}>
      {heights.map((h, i) => (
        <span
          key={i}
          className={`nut-w-[3px] nut-bg-green-500 nut-rounded nut-flex-shrink-0`}
          style={{ height: h, minWidth: "1px" }}
        />
      ))}
    </div>
  );
};

interface VoiceChatFooterProps {
  onShoppingClick?: () => void;
  cartItems: number;
  changeModeDisabled?: boolean;
}

export const ChatFooterVoice = memo(function VoiceChatFooter({
  onShoppingClick,
  cartItems,
  changeModeDisabled,
}: VoiceChatFooterProps) {
  const {
    setChatMode,
    isMicrophoneEnabled,
    setIsMicrophoneEnabled,
    setMicPermission,
  } = useGlobalState();
  const { localParticipant } = useLocalParticipant();
  const [isUpdatingMicrophone, setIsUpdatingMicrophone] = useState(false);
  const isUserSpeaking = useIsUserSpeaking();
  const roundButtonClasses =
    "nut-w-[68px] nut-h-[68px] nut-flex-shrink-0 nut-cursor-pointer nut-flex nut-items-center nut-justify-center nut-rounded-[50%] nut-border-2 nut-border-[#D8D8D8] disabled:nut-cursor-wait";

  return (
    <div className="voice-chat-footer nut-w-[100%] nut-flex nut-items-center nut-justify-between nut-gap-x-2 nut-mt-3">
      <button
        className={roundButtonClasses}
        disabled={changeModeDisabled}
        onClick={() => {
          setChatMode("text");
        }}
      >
        <img
          src={commentIcon}
          alt="comment-icon"
          className="nut-w-[23.3px] nut-h-[22.17]"
        />
      </button>

      {isUserSpeaking && isMicrophoneEnabled ? (
        <MicSpeaking />
      ) : (
        <MicSilent />
      )}

      <button
        className={twMerge(roundButtonClasses, "nut-relative")}
        onClick={onShoppingClick}
      >
        {cartItems > 0 && (
          <div className="nut-absolute -nut-top-1 -nut-right-1 nut-bg-magic-gradient nut-text-white nut-rounded-full nut-w-6 nut-h-6 nut-flex nut-items-center nut-justify-center nut-text-sm">
            {cartItems}
          </div>
        )}
        <img
          src={shoppingBasket}
          title="Open basket"
          alt="shopping-basket"
          className="nut-w-[23.3px] nut-h-[22.17]"
        />
      </button>
      <button
        className={roundButtonClasses}
        disabled={isUpdatingMicrophone}
        onClick={async () => {
          if (isUpdatingMicrophone) return;

          const shouldEnable = !isMicrophoneEnabled;
          setIsUpdatingMicrophone(true);
          setIsMicrophoneEnabled(shouldEnable);

          try {
            // Keep the browser permission request in the click handler. This is
            // required by Safari and prevents the global sync effect from
            // racing an indirect toggle while Chrome is showing its prompt.
            await localParticipant.setMicrophoneEnabled(shouldEnable);

            if (shouldEnable) {
              setMicPermission("granted");
            }
          } catch (error) {
            setIsMicrophoneEnabled(false);
            setMicPermission("denied");
            captureError("Failed to enable microphone", error);
          } finally {
            setIsUpdatingMicrophone(false);
          }
        }}
      >
        {!isMicrophoneEnabled ? (
          <div className="nut-relative nut-w-[28px] nut-h-[28px] nut-opacity-40">
            <img
              src={microphone}
              title="Enable microphone"
              alt="Microphone disabled"
              className="nut-w-full nut-h-full nut-opacity-80 nut-cursor-pointer"
            />
            <div className="nut-absolute nut-top-0 nut-text-white nut-left-0 nut-w-[140%] nut-h-[2px] nut-bg-black nut-transform -nut-translate-y-1/2 nut-rotate-45 nut-origin-left">
              .
            </div>
          </div>
        ) : (
          <div className="nut-relative nut-w-[28px] nut-h-[28px]">
            <img
              src={microphone}
              alt="Microphone enabled"
              title="Disable microphone"
              className="nut-w-full nut-h-full nut-cursor-pointer"
            />
          </div>
        )}
      </button>
    </div>
  );
});
